import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HttpClient } from '@angular/common/http';

import { JwtModule, JwtInterceptor } from '@auth0/angular-jwt';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
    TranslateModule,
    TranslateLoader,
    MissingTranslationHandler
} from '@ngx-translate/core';

import { environment } from '../environments/environment';
import { ApiTranslateLoader } from './shared/apiTranslationLoader';
import { ApiMissingTranslationHandler } from './shared/apiMissingTranslationHandler';

import { httpInterceptorProviders } from './shared/http-interceptors';
import { resolverProviders } from './shared/resolvers';

import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './shared/shared.module';
import { MainModule } from './main/main.module';
import { AuthModule } from './auth/auth.module';
import { ParametersModule } from './parameters/parameters.module';
import { LicenceModule } from './licence/licence.module';
import { ReportModule } from './reports/report.module';
import { HelpModule } from './help/help.module';
import { UploadModule } from './upload/upload.module';
import { AccountModule } from './user/account/account.module';
import { MonitoringModule } from './monitoring/monitoring.module';

import { AppComponent } from './app.component';
import { ErrorComponent } from './error/error.component';
import { AppConfigService } from './shared/services/app-config.service';
import { SimulationModule } from './simulation/simulation.module';
import { HomeModule } from './home/home.module';
import { WikiModule } from './wiki/wiki.module';
import { BizzmineModule } from './bizzmine/bizzmine.module';
import { SimplebarAngularModule } from 'simplebar-angular';
import { DashboardModule } from './dashboard/dashboard.module';

export function tokenGetter(): string {
    return localStorage.getItem(environment.tokenKey);
}

// AoT requires an exported function for factories
export function TranslateLoaderFactory(http: HttpClient): ApiTranslateLoader {
    return new ApiTranslateLoader(http);
}

// Load config files before app start
export function ConfigFactory(config: AppConfigService) {
    return () => config.loadConfig();
}

// Load translations before app start
export function TranslationsFactory(config: AppConfigService) {
    return () => config.loadTranslations();
}

@NgModule({
    declarations: [
        AppComponent,
        ErrorComponent,
    ],
    imports: [
        SimplebarAngularModule,
        BrowserModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        HttpClientModule,
        AppRoutingModule,
        SharedModule,
        MainModule,
        AuthModule,
        HomeModule,
        SimulationModule,
        ParametersModule,
        LicenceModule,
        ReportModule,
        AccountModule,
        HelpModule,
        UploadModule,
        MonitoringModule,
        FontAwesomeModule,
        WikiModule,
        BizzmineModule,
        DashboardModule,

        JwtModule.forRoot({
            config: {
                tokenGetter: tokenGetter,
                whitelistedDomains: [environment.apiDomain],
                blacklistedRoutes: [environment.identityApiUrl + 'connect/token']
            }
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: TranslateLoaderFactory,
                deps: [HttpClient]
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: ApiMissingTranslationHandler
            }
        })
    ],
    providers: [
        httpInterceptorProviders,
        JwtInterceptor,
        resolverProviders,
        {
            provide: APP_INITIALIZER,
            useFactory: ConfigFactory,
            deps: [AppConfigService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: TranslationsFactory,
            deps: [AppConfigService],
            multi: true
        }
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
